import type { ThreatModelWorkspace } from "../../api/types.js";
import { snapshotHeadline } from "./threat-model-projection.js";
import "./threat-model-snapshot-graph.css";

const WIDTH = 960;
const HEIGHT = 280;

export function ThreatModelSnapshotGraph({ workspace }: { workspace: ThreatModelWorkspace }) {
  const { graph, attackPaths, omissions } = workspace.projection;
  const path = attackPaths.items[0];
  const pathIds = path?.nodeIds ?? [];
  const pathSet = new Set(pathIds);
  const spine = pathIds.length
    ? pathIds
        .map((id) => graph.nodes.find((node) => node.id === id))
        .filter((node): node is (typeof graph.nodes)[number] => Boolean(node))
    : graph.nodes;
  const extras = pathIds.length ? graph.nodes.filter((node) => !pathSet.has(node.id)) : [];
  const spineXs = spine.map(
    (_, index) => 120 + index * Math.min(180, 520 / Math.max(1, spine.length)),
  );
  const edgePairs = graph.edges
    .map((edge) => {
      const fromIndex = spine.findIndex((node) => node.id === edge.fromNodeId);
      const toIndex = spine.findIndex((node) => node.id === edge.toNodeId);
      if (fromIndex < 0 || toIndex < 0) return null;
      return { id: edge.id, x1: spineXs[fromIndex] ?? 120, x2: spineXs[toIndex] ?? 300 };
    })
    .filter((item): item is { id: string; x1: number; x2: number } => Boolean(item));
  const headline = snapshotHeadline({
    nodeCount: graph.nodes.length,
    edgeCount: graph.edges.length,
    pathCount: attackPaths.items.length,
    omittedCount: omissions.length,
  });

  return (
    <section
      className="threat-model-snapshot-graph"
      data-testid="threat-model-snapshot-graph"
      aria-label="Snapshot threat graph"
    >
      <div className="threat-model-snapshot-graph__header">
        <div>
          <span className="page-kicker">This frozen snapshot</span>
          <h3>What the evidence will admit</h3>
          <p>{headline}</p>
        </div>
      </div>
      <div
        className="threat-model-snapshot-graph__board"
        style={{ aspectRatio: `${WIDTH} / ${HEIGHT}` }}
      >
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} aria-hidden="true">
          <rect
            className="threat-model-snapshot-graph__zone"
            x="24"
            y="16"
            width="600"
            height="248"
            rx="12"
          />
          <text className="threat-model-snapshot-graph__zone-label" x="40" y="38">
            Proved
          </text>
          <rect
            className="threat-model-snapshot-graph__zone"
            x="648"
            y="16"
            width="288"
            height="248"
            rx="12"
          />
          <text className="threat-model-snapshot-graph__zone-label" x="664" y="38">
            Omitted
          </text>
          {edgePairs.map((edge) => (
            <g key={edge.id}>
              <path
                className="threat-model-snapshot-graph__halo"
                d={`M ${edge.x1} 148 L ${edge.x2} 148`}
              />
              <path
                className="threat-model-snapshot-graph__edge"
                d={`M ${edge.x1} 148 L ${edge.x2} 148`}
              />
            </g>
          ))}
        </svg>
        {spine.map((node, index) => (
          <div
            key={node.id}
            className={
              pathSet.has(node.id)
                ? "threat-model-snapshot-graph__node is-path"
                : "threat-model-snapshot-graph__node"
            }
            style={{
              left: `${((spineXs[index] ?? 120) / WIDTH) * 100}%`,
              top: "52%",
            }}
          >
            <span className="threat-model-snapshot-graph__disc">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="threat-model-snapshot-graph__title">{node.name}</span>
          </div>
        ))}
        {extras.map((node, index) => (
          <div
            key={node.id}
            className="threat-model-snapshot-graph__node"
            style={{
              left: `${((140 + index * 90) / WIDTH) * 100}%`,
              top: "78%",
            }}
          >
            <span className="threat-model-snapshot-graph__disc">·</span>
            <span className="threat-model-snapshot-graph__title">{node.name}</span>
          </div>
        ))}
        <div
          className="threat-model-snapshot-graph__omitted"
          style={{ left: "82%", top: "54%" }}
          data-testid="threat-model-omitted-cloud"
        >
          <span>Kept out of the model</span>
          <strong>{omissions.length}</strong>
          <p>Visible records that were not strong enough to become a path hop.</p>
        </div>
      </div>
      <ul className="threat-model-snapshot-graph__legend">
        <li>Gold is a validated edge on this snapshot.</li>
        <li>Lapis is omitted evidence — not a hop.</li>
        <li>No gold route means no complete path is claimed.</li>
      </ul>
    </section>
  );
}
